/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { App, AppInfo } from '@capacitor/app';
import semver from 'semver';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { Input, Popover, Image } from 'antd';
import { WebViewCache } from 'capacitor-plugin-webview-cache';
import { Player } from 'video-react';
import { Device, DeviceInfo } from '@capacitor/device';
import { Keyboard } from '@capacitor/keyboard';
import { klona } from 'klona';
import { TrackerCustomEvent } from 'common-package';
import { nanoid } from '@reduxjs/toolkit';
import { useGetState, useLockFn, useSetState, useUpdateEffect } from 'ahooks';
import { Dialog } from 'antd-mobile';
import dayjs, { Dayjs } from 'dayjs';
import { useDevice, useNav, useTracker } from '@/hooks';
import HeaderMask from '@/pages/user/member-management/components/HeaderMask';
import spinImg from '@/assets/image/spin.png';
import service from '@/service';
import { bytesToMB, uploadFileCommon } from '@/utils';
import { ALL_PAGES, BODY_PART, COMMON_VIDEO_POSTER, DAYJS_FORMAT, MassageType2Text } from '@/constant';
import { Header, Toast, Calendar, ScrollView, When } from '@/components';
import { getMediaFormat } from '../feedback-progress';
import SelectOrder, { SelectOrderRefAttrs, STATES_FILTER } from './select-order';

import cx from './index.module.scss';

interface Props {}

type ErrInfo = Record<'contact' | 'desc', string>;

type MediaType = 'image' | 'video';

type UploadFiles = Array<{
  type: MediaType;
  url: string;
  nanoid: string;
  startedUpload: boolean;
}>;

enum OrderTitle {
  Abnormal = '最近异常订单',
  Serving = '当前设备服务中订单',
}

export const showTimeText = (timeStamp: number) => {
  if (!timeStamp) return '';
  const isToday = dayjs().format(DAYJS_FORMAT.DATE) === dayjs(timeStamp).format(DAYJS_FORMAT.DATE);
  return dayjs(timeStamp).format(isToday ? 'YYYY-MM-DD（今天） HH:mm' : DAYJS_FORMAT.MINUTES);
};
``;

const orderTimeText = (recentAbnormalOrder: GetLastAbnormalOrderResponse) => {
  if (recentAbnormalOrder?.state === 'Abnormal') {
    return `异常时间：${showTimeText(recentAbnormalOrder?.abnormalTime)}`;
  } else if (recentAbnormalOrder?.state === 'Serving') {
    return `下单时间：${showTimeText(recentAbnormalOrder?.createTime)}`;
  }
};

const IssueReport: React.FC<Props> = () => {
  const { sendEvent } = useTracker();
  const nav = useNav();
  const [contactNumber, setContactNumber, getContactNumber] = useGetState('');
  const [feedbackTime, setFeedbackTime] = useState<Dayjs>(dayjs());
  const [descStr, setDescStr] = useState('');
  const [selectOrderInfo, setSelectOrderInfo] = useState<GetFinishedOrderListResponse>({} as any);
  const [errInfo, setErrInfo] = useSetState<ErrInfo>({} as any);
  const [isInitingData, setIsInitingData] = useState(true);
  const selectOrderRef = useRef<SelectOrderRefAttrs>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLimitTipPopoverOpen, setIsLimitTipPopoverOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const isHandleReported = useRef(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFiles>([]);
  const isShowBackConfirm = useRef(false);
  const cancelTokenControllers = useRef<Record<string, AbortController>>({});
  const playerRef = useRef(null);
  const [appInfo, setAppInfo] = useState<AppInfo>(null);
  const [isKeyBoardShow, setIsKeyBoardShow] = useState(false);
  const [recentAbnormalOrder, setRecentAbnormalOrder] = useState<GetLastAbnormalOrderResponse>(null);
  const currentOpPlatform = useRef<DeviceInfo['platform']>(null);

  const { checkIsApp } = useDevice({
    onGetDeviceInfo: info => {
      if (info?.platform === 'android') {
        App.getInfo()
          .then(setAppInfo)
          .catch(e => console.error('getInfo error', e));
      }
    },
  });

  const isDisabledSubmit = useMemo(
    () => !(contactNumber && descStr && feedbackTime),
    [contactNumber, descStr, feedbackTime]
  );

  const currentHasFileInLoading = useMemo(() => uploadFiles?.some(item => !item.url), [uploadFiles]);

  const initData = useCallback(async () => {
    try {
      setIsInitingData(true);

      const { data: lastAbnormalData } = await service.service.getLastAbnormalOrder();

      const { data: reportInfo } = await service.service.getReportInfo(lastAbnormalData?.id);
      setRecentAbnormalOrder({
        ...lastAbnormalData,
        deviceId: reportInfo?.deviceId,
      });
    } finally {
      setTimeout(() => setIsInitingData(false), 100);
    }
  }, []);

  const handleSubmit = useLockFn(
    useCallback(async () => {
      if (isDisabledSubmit) return;

      if (isHandleReported.current) return;

      if (!contactNumber) {
        setErrInfo({ contact: '请输入手机号' });
        !descStr && setErrInfo({ desc: '请输入问题描述' });
        return;
      }

      if (!descStr) {
        setErrInfo({ desc: '请输入问题描述' });
        return;
      }

      if (errInfo?.contact || errInfo?.desc) {
        return;
      }

      if (currentHasFileInLoading) {
        Toast.show('请文件上传完成后再提交');
        return;
      }

      try {
        sendEvent(TrackerCustomEvent.SubmitIssueClk, { et: 'CLK' });

        isHandleReported.current = true;
        Toast.loading('正在提交');
        await service.service.reportAbnormalIssue({
          serviceOrderId: selectOrderInfo?.id || null,
          phoneNumber: contactNumber,
          remark: descStr,
          imageUrls: uploadFiles?.map(item => item.url)?.filter(Boolean),
          abnormalTime: dayjs(feedbackTime).valueOf(),
        });
        nav(ALL_PAGES.HELP_FEEDBACK);
        Toast.success('提交成功');
      } catch (e) {
        isHandleReported.current = false;
      }
    }, [
      contactNumber,
      currentHasFileInLoading,
      descStr,
      errInfo,
      feedbackTime,
      isDisabledSubmit,
      nav,
      selectOrderInfo?.id,
      sendEvent,
      setErrInfo,
      uploadFiles,
    ])
  );

  // 上传媒体文件
  const uploadMedia = useCallback(
    (type: MediaType) => async () => {
      let result = await FilePicker[type === 'image' ? 'pickImages' : 'pickVideos']();
      result?.files?.map(async file => {
        if (bytesToMB(file.size) > (type === 'image' ? 10 : 50)) {
          result = null;
          file = null;
          Toast.show(type === 'image' ? '单个图片大小不超过10m' : '单个视频大小不超过50m');

          WebViewCache.clearCache().catch(e => {
            console.error('clear error', e);
          });
          return;
        }
        const id = nanoid(6);
        const cancelToken = new AbortController();
        cancelTokenControllers.current[id] = cancelToken;

        setUploadFiles(prev => [
          ...prev,
          {
            type,
            nanoid: id,
            startedUpload: false,
            url: '',
          },
        ]);

        try {
          const downloadUrl = await uploadFileCommon(file?.blob);
          // 本地文件读取完成，设置上传标志位
          setUploadFiles(prev => {
            const backUpValue = klona(prev);
            const currentItem = backUpValue.find(item => item.nanoid === id);
            if (!currentItem) return prev;

            currentItem.startedUpload = true;
            return backUpValue;
          });

          setUploadFiles(prev => {
            const backUpValue = klona(prev);
            const currentItem = backUpValue.find(item => item.nanoid === id);
            if (!currentItem) return prev;

            currentItem.url = downloadUrl;
            return backUpValue;
          });
        } catch (error) {
          if (error?.message?.includes('status code 413')) {
            setTimeout(() => Toast.show(type === 'image' ? '单个图片大小不超过10m' : '单个视频大小不超过50m'), 10);
          }
          if (error?.code === 'ERR_CANCELED') {
            setTimeout(() => Toast.show('取消上传'), 10);
          }
          console.error('upload occur error', error);
          setUploadFiles(prev => prev.filter(item => item.nanoid !== id));
        } finally {
          result = null;
          file = null;
          WebViewCache.clearCache().catch(e => {
            console.error('clear error', e);
          });
        }
      });
    },
    []
  );

  const backConfirm = useCallback(() => {
    if (isShowBackConfirm.current) return;
    isShowBackConfirm.current = true;
    Dialog.confirm({
      destroyOnClose: true,
      title: `确认要退出编辑吗？`,
      content: '确认后会自动清除此次编辑',
      confirmText: '确认退出',
      onConfirm: () => {
        nav(ALL_PAGES.HELP_FEEDBACK);
      },
      onCancel: () => {
        isShowBackConfirm.current = false;
      },
    });
  }, [nav]);

  const initListener = useCallback(async () => {
    currentOpPlatform.current = (await Device.getInfo())?.platform;
    if (currentOpPlatform.current === 'android') {
      Keyboard.addListener('keyboardWillShow', () => {
        setIsKeyBoardShow(true);
      });
      Keyboard.addListener('keyboardWillHide', () => {
        setIsKeyBoardShow(false);
      });
    }
  }, []);

  const handleReportImmediately = useCallback(() => {
    setSelectOrderInfo(recentAbnormalOrder);
  }, [recentAbnormalOrder]);

  useUpdateEffect(() => {
    if (!new RegExp(/^1[3-9]\d{9}$/).test(contactNumber)) {
      setErrInfo({ contact: '请输入正确的手机号' });
    } else {
      setErrInfo({ contact: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactNumber]);

  useEffect(() => {
    if (selectOrderInfo?.id) {
      // 当前未填写手机号才执行自动填充手机号
      if (!getContactNumber()) {
        service.service.getReportInfo(selectOrderInfo?.id).then(info => {
          info?.data?.phoneNumber && setContactNumber(info.data.phoneNumber);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectOrderInfo]);

  useEffect(() => {
    initData();

    initListener();
    return () => {
      if (currentOpPlatform.current === 'android') {
        Keyboard.removeAllListeners();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('issue-report')}>
      <Header
        backConfig={{
          text: '返回',
          action: backConfirm,
        }}
        title="问题反馈"
      />
      <SelectOrder ref={selectOrderRef} onSelectOrder={orderInfo => setSelectOrderInfo(klona(orderInfo))} />
      <ScrollView autoRefresh className={cx('form-wrapper')}>
        <div className={cx('form-inner-wrapper')}>
          <When condition={!!recentAbnormalOrder?.id && !selectOrderInfo?.id}>
            <div className={cx('in-serving-order-box')}>
              <div className={cx('header')}>
                <div className={cx('title')}>{OrderTitle[recentAbnormalOrder?.state]}</div>
                <div className={cx('report-text')} onClick={handleReportImmediately}>
                  立即上报
                </div>
              </div>
              <div className={cx('content')}>
                <div className={cx('plan-name')}>
                  <div>{recentAbnormalOrder?.goodsName}</div>
                  <div className={cx('serving-span', recentAbnormalOrder?.state)}>
                    {STATES_FILTER.find(item2 => item2.value === recentAbnormalOrder?.state)?.stateText}
                  </div>
                </div>
                <div className={cx('therapy-type')}>
                  {BODY_PART[recentAbnormalOrder?.bodyPart] ? `${BODY_PART[recentAbnormalOrder?.bodyPart]} | ` : ''}
                  {MassageType2Text[recentAbnormalOrder?.itemType]}
                </div>
                <div className={cx('order-time')}>{orderTimeText(recentAbnormalOrder)}</div>
              </div>
            </div>
          </When>
          <div
            className={cx('form-container')}
            onClick={() => {
              setIsPopoverOpen(false);
              setIsLimitTipPopoverOpen(false);
            }}
          >
            <div className={cx('form-box')}>
              <div className={cx('submit', { disabled: isDisabledSubmit })} onClick={handleSubmit}>
                提交
              </div>
              <div className={cx('form-items')}>
                <div className={cx('form-item')}>
                  <div className={cx('label')}>订单：</div>
                  <div
                    className={cx('select-order')}
                    onClick={() => {
                      if (!isInitingData) {
                        selectOrderRef.current.show(selectOrderInfo);
                      }
                    }}
                  >
                    <div className={cx('order-name')}>
                      <div className={cx('name-text')}>
                        {selectOrderInfo?.id ? (
                          selectOrderInfo?.goodsName
                        ) : (
                          <span className={cx('placeholder')}>请选择</span>
                        )}
                      </div>
                      <Icon name="arrow-right" className={cx('icon')} />
                    </div>
                    {!!selectOrderInfo?.id && (
                      <>
                        <div className={cx('plan-info')}>
                          {BODY_PART[selectOrderInfo?.bodyPart] ? `${BODY_PART[selectOrderInfo?.bodyPart]} | ` : ''}
                          {MassageType2Text[selectOrderInfo?.itemType]}
                        </div>
                        <div className={cx('device-info')}>设备：{selectOrderInfo?.deviceId}</div>
                      </>
                    )}
                  </div>
                </div>
                <div className={cx('form-item')}>
                  <div className={cx('label')}>
                    <div className={cx('required')}>必填项</div>
                    <div className={cx('label-text')}>联系方式：</div>
                    {errInfo?.contact && <div className={cx('invalid-info')}>{errInfo.contact}</div>}
                  </div>
                  <div className={cx('contact-info')}>
                    <Input
                      value={contactNumber}
                      allowClear
                      placeholder="请输入"
                      maxLength={11}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const { value: inputValue } = e.target;
                        const reg = /^\d{1,11}$/;
                        if (reg.test(inputValue) || inputValue === '') {
                          setContactNumber(inputValue);
                        }
                      }}
                    />
                  </div>
                </div>
                <div className={cx('form-item')}>
                  <div className={cx('label')}>
                    <div className={cx('required')}>必填项</div>
                    <div className={cx('label-text')}>问题出现时间：</div>
                  </div>
                  <div className={cx('contact-info')}>
                    <Calendar
                      className={cx('calendar-wrapper')}
                      placeholder="请输入日期"
                      mode="minutes"
                      onChange={v => setFeedbackTime(v as Dayjs)}
                      value={feedbackTime}
                      disabledDate={current => current && current > dayjs().endOf('day')}
                    />
                  </div>
                </div>
                <div className={cx('form-item')}>
                  <div className={cx('label')}>
                    <div className={cx('required')}>必填项</div>
                    <div className={cx('label-text')}>问题描述：</div>
                    {errInfo?.desc && <div className={cx('invalid-info')}>{errInfo.desc}</div>}
                  </div>
                  <div className={cx('issue-desc')}>
                    <Input.TextArea
                      value={descStr}
                      style={{ height: 120, resize: 'none' }}
                      placeholder="请输入您所需要反馈的问题"
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        const value = e.target.value;
                        if (value?.length <= 200) {
                          setDescStr(value);
                        } else {
                          Toast.show('超出文字限制');
                        }

                        setErrInfo({ desc: value?.trim() ? null : '请输入问题描述' });
                      }}
                    />
                    <div className={cx('text-num')}>
                      <span className={cx('current-num')}>{descStr?.length || 0}</span>
                      <span>/200</span>
                    </div>
                  </div>
                </div>
                <div className={cx('add-media')}>
                  <div className={cx('preview-box')}>
                    {uploadFiles.map((item, index) => (
                      <div
                        key={item.url}
                        className={cx('media-item')}
                        onClick={() => {
                          setPreviewIndex(index);
                          setIsPreviewVisible(true);
                        }}
                      >
                        {item.type === 'image' && !!item.url && <img className={cx('image')} src={item.url} alt="" />}
                        {item.startedUpload && (
                          <Icon
                            name="close"
                            onClick={e => {
                              e?.stopPropagation();

                              // 取消正在上传的请求
                              if (cancelTokenControllers.current?.[item.nanoid]) {
                                cancelTokenControllers.current[item.nanoid].abort?.();
                                cancelTokenControllers.current[item.nanoid] = null;
                                Reflect.deleteProperty(cancelTokenControllers.current, item.nanoid);
                              }

                              setUploadFiles(prev => prev.filter(item2 => item2.nanoid !== item.nanoid));
                            }}
                            className={cx('close-icon')}
                          />
                        )}
                        {!item.url && (
                          <div className={cx('loading-wrapper')}>
                            <img className={cx('spin-img')} src={spinImg} alt="" />
                          </div>
                        )}
                        {item.type === 'video' && !!item.url && (
                          <div className={cx('video-space')}>
                            <Icon name="video" size={28} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className={cx('add-container')}>
                    <Popover
                      open={isPopoverOpen}
                      getPopupContainer={container => container}
                      content={
                        <div className={cx('content-wrapper')}>
                          <div className={cx('content-item')} onClick={uploadMedia('image')}>
                            图片
                          </div>
                          <div className={cx('content-item')} onClick={uploadMedia('video')}>
                            视频
                          </div>
                        </div>
                      }
                      title={null}
                      arrow={false}
                    >
                      <Popover
                        open={isLimitTipPopoverOpen}
                        getPopupContainer={container => container}
                        color="#212121"
                        content={
                          <div style={{ padding: '5px 20px', color: '#fff', fontSize: '14px' }}>文件上传限额已满</div>
                        }
                        title={null}
                      >
                        <div className={cx('add-icon', { disabled: uploadFiles?.length >= 3 })}>
                          <Icon
                            name="add-circle"
                            onClick={() => {
                              if (uploadFiles?.length >= 3) {
                                setTimeout(() => setIsLimitTipPopoverOpen(true), 10);
                                return;
                              }

                              // if (!checkIsApp()) {
                              //   Toast.show('仅可在PAD端上传图片或视频');
                              //   return;
                              // }
                              if (checkIsApp()) {
                                if (semver.lt(appInfo?.version, '1.10.0')) {
                                  Toast.show('请升级理疗师工作台至 v1.10.0 或以上版本以支持上传图片或视频');
                                  return;
                                }
                              }

                              if (currentHasFileInLoading) {
                                Toast.show('请等待上一图片或视频上传完成后再添加');
                                return;
                              }

                              setTimeout(() => setIsPopoverOpen(!isPopoverOpen), 0);
                            }}
                          />
                        </div>
                      </Popover>
                    </Popover>
                    <div className={cx('add-tip-text')}>
                      <div className={cx('main-text')}>添加图片或视频</div>
                      <div className={cx('sub-text')}>建议视频不超过1分钟</div>
                    </div>
                  </div>
                </div>
                <Image.PreviewGroup
                  items={uploadFiles?.map(item => item.url)?.filter(Boolean)}
                  preview={{
                    visible: isPreviewVisible,
                    movable: false,
                    current: previewIndex,
                    onChange: index => setPreviewIndex(index),
                    onVisibleChange: visible => {
                      setIsPreviewVisible(visible);
                      !visible && playerRef.current?.actions?.pause?.();
                    },
                    imageRender: (node, info) => {
                      // @ts-ignore
                      if (getMediaFormat(info?.image?.url) === 'image') {
                        return node;
                      } else {
                        return (
                          <Player
                            ref={playerRef}
                            poster={COMMON_VIDEO_POSTER}
                            // @ts-ignore
                            className={cx('custom-video-player')}
                            // @ts-ignore
                            src={info?.image?.url}
                            aspectRatio="16:9"
                          />
                        );
                      }
                    },
                    toolbarRender: () => null,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </ScrollView>
      <HeaderMask show={isKeyBoardShow} />
    </div>
  );
};

export default IssueReport;
